import React, { useEffect, useId, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';

// Asegúrate de que esta mutación esté configurada para aceptar un FormData.
// En tu `workhopsApiSlice`, el builder.mutation debe usar `prepareHeaders`
// o no especificar Content-Type para solicitudes con FormData, ya que el navegador lo hará.
import { useCreateWorkshopsMutation } from '../../../store/api/endpoints/workhopsApiSlice';

import { openFeedback } from '../../../store/slices/feedback';

import HeaderVista from '../../elementos/HeaderVista';
import Input from '../../elementos/Input';
import CropImageNew from '../../elementos/CropImageNew';
import Loading from '../../elementos/Loading';

import Button from '@mui/material/Button';
import { Breadcrumbs, Divider, Typography } from '@mui/material';
// Usar Grid directamente de @mui/material/Grid
import Grid from '@mui/material/Grid2';

import EventIcon from '@mui/icons-material/Event';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import CheckIcon from '@mui/icons-material/Check';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import NotesIcon from '@mui/icons-material/Notes';
import VideoUploader from '../../elementos/VideoUploader';

export default function WorkshopsCrear() {

    const navigate = useNavigate();
    const dispatch = useDispatch();

    // ID para asociar el video al objectId que no existe aún
    const idObjeto = useId();

    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [speaker, setSpeaker] = useState('');
    const [publicationDate, setPublicationDate] = useState('');
    // Ahora estos estados almacenarán el objeto File
    const [videoFile, setVideoFile] = useState(null);
    const [previewImageFile, setPreviewImageFile] = useState(null);

    const [
        crearRegistro,
        {
            data: responseCrearRegistro,
            isUninitialized: isUninitializedCrearRegistro,
            isLoading: isLoadingCrearRegistro,
            isSuccess: isSuccessCrearRegistro,
            isError: isErrorCrearRegistro,
            error: errorDataCrearRegistro,
        }
    ] = useCreateWorkshopsMutation();


    // Función callback para cuando el VideoUploader devuelve un archivo
    const handleVideoSelected = (url) => {
        setVideoFile(url)
        //console.log("Video seleccionado:", file ? file.name : "ninguno");
    };

    // Función callback para cuando el ImageCropper devuelve un archivo
    const handlePreviewImageCropped = (file) => {
        setPreviewImageFile(file);
        //console.log("Imagen croppeada y lista:", file ? file.name : "ninguna");
    };

    const handleSubmit = async (e) => { // Marca como async porque la mutación podría ser async
        e.preventDefault();

        // 1. Crear un objeto FormData
        const formData = new FormData();

        // 2. Adjuntar los campos de texto
        formData.append('title', title);
        formData.append('description', description);
        formData.append('speaker', speaker);
        formData.append('videoId', idObjeto);
        formData.append('publicationDate', publicationDate); // Asegúrate que publicationDate esté en un formato que el backend espere, ej. "YYYY-MM-DD"

        if (previewImageFile) {
            formData.append('previewImage', previewImageFile);
        } else {
            // Manejo si la imagen de vista previa es requerida pero no se ha seleccionado
            dispatch(openFeedback({
                severity: 'error',
                message: 'La imagen de vista previa es un campo requerido.'
            }));
            return; // Detener el envío si la imagen es requerida
        }

        // 4. Llamar a la mutación con el FormData
        crearRegistro(formData); // Pasa el FormData directamente como argumento.
    };

    useEffect(() => {
        if (isErrorCrearRegistro) {
            dispatch(openFeedback({
                severity: 'error',
                message: errorDataCrearRegistro.data?.message || "Ocurrió un error al crear el workshop."
            }));
        }
    }, [isErrorCrearRegistro, errorDataCrearRegistro, dispatch]);

    useEffect(() => {
        if (isSuccessCrearRegistro) {
            dispatch(openFeedback({
                severity: 'success',
                message: "¡Registro creado!"
            }));
            navigate("/workshops");
        }
    }, [isSuccessCrearRegistro, dispatch, navigate]);

    if (isLoadingCrearRegistro) return (<Loading />);

    return (
        <>
            <HeaderVista
                titulo={
                    <Breadcrumbs aria-label="breadcrumb">
                        <Link to="/workshops" style={{ textDecoration: 'none', color: 'inherit' }}> {/* Añadir estilo para Link */}
                            <Typography color="text.primary">Workshops</Typography>
                        </Link>
                        <Typography color="text.primary">Crear</Typography>
                    </Breadcrumbs>
                }
                icon={<OndemandVideoIcon />}
            />
            <form onSubmit={handleSubmit}>
                <Grid container rowSpacing={2} columnSpacing={1}>

                    <Grid size={{ xs: 12, md: 4 }}> {/* Usar xs, md directamente */}
                        <Input
                            label="Fecha Publicación"
                            icon={<EventIcon />}
                            value={publicationDate}
                            onChange={setPublicationDate}
                            type="date"
                            required
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Input
                            label="Título"
                            icon={<OndemandVideoIcon />}
                            value={title}
                            onChange={setTitle}
                            type="text"
                            required
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Input
                            label="Speaker"
                            icon={<RecordVoiceOverIcon />}
                            value={speaker}
                            onChange={setSpeaker}
                            type="text"
                            required
                        />
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Input
                            label="Descripción"
                            icon={<NotesIcon />}
                            value={description}
                            onChange={setDescription}
                            type="text"
                            multiline
                            required
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }} align="center"> </Grid>
                    <Grid size={{ xs: 12, md: 2 }} align="center">
                        <CropImageNew
                            imagen={null} // Si estás creando, no hay imagen previa. Si es para editar, pasa la URL aquí.
                            onImageCropped={handlePreviewImageCropped} // Nuevo callback
                            maxSizeMB={1} // Ajusta el tamaño máximo de la imagen croppeada
                            width={500}
                            height={300} // Ajusta estas dimensiones a lo que esperas para la vista previa
                            // ruta="descuentos" // Esta prop ya no es necesaria en ImageCropper
                            // formatosPermitidos="image/jpeg, image/png" // Esta prop ya no es necesaria en ImageCropper
                            avatar={false} // Si es una imagen de vista previa, no suele ser un avatar
                        />
                        {previewImageFile && (
                            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                Archivo de imagen seleccionado: <strong>{previewImageFile.name}</strong>
                            </Typography>
                        )}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }} align="center">
                        <VideoUploader
                            onVideoSelected={handleVideoSelected} // Nuevo callback
                            idObjeto={idObjeto}
                            buttonText="Seleccionar Archivo de Video"
                        />
                        {videoFile && (
                            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                                Archivo de video seleccionado: <strong>{videoFile.name}</strong>
                            </Typography>
                        )}
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Divider sx={{ my: 3 }} /> {/* Margen para el divisor */}
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                        <Button type="submit" variant="contained" color="success" startIcon={<CheckIcon />} disabled={isLoadingCrearRegistro}>
                            Guardar
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </>
    );
}
